import { ControlChartEventsDataGridApi } from "src/api/generated/spc/controlChart/api/controlChartEventsDataGridApi.ts";
import { ControlledAsyncDataGrid } from "src/components/common/dataGrid/ControlledAsyncDataGrid.tsx";
import { dateTimeColumn, integerColumn, textColumn } from "src/components/common/dataGrid/columns.tsx";
import i18n from "i18next";
import { DocumentsOfObjectButton } from "src/components/views/documents/objectDocuments/DocumentsOfObjectButton.tsx";
import { nullableAavoObjectRef } from "src/utils/aavoObjectRefUtils.ts";
import { useContext } from "react";
import { ControlChartEventsDataGridApi_GridRow } from "src/api/generated/spc/controlChart/api/controlChartEventsDataGridApi.ts";
import { useServerSideDataGridModel } from "src/components/common/dataGrid/gridModel/useServerSideDataGridModel";
import { useTenantCustomizations } from "src/tenantCustomizations/TenantCustomizationsContext.ts";
import {
	FocusControlChartGraphViewToRecordButton,
	UploadDocumentsToSpcRecordButton,
} from "src/components/views/spc/controlChart/controlChartInspectingView/rawRecordDataGridUtils.tsx";
import { parseDayJsNullable } from "src/utils/dayjsUtils.ts";
import { RefreshableElementProps } from "src/utils/useRefreshRef.ts";
import { ControlChartInspectingViewContext } from "src/components/views/spc/controlChart/controlChartInspectingView/ControlChartInspectingViewContext.ts";
import { AsyncMenuButton } from "src/components/common/contextMenu/AsyncMenuButton.tsx";
import { faLink } from "@fortawesome/pro-regular-svg-icons";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";
import { useGenericDialog } from "src/components/common/dialogs/GenericDialogContext.ts";
import { ConnectProcessImprovementToRawRecordView } from "src/components/views/spc/processImprovement/ConnectProcessImprovementToRawRecordView.tsx";
import { ProcessImprovementsView } from "src/components/views/spc/processImprovement/ProcessImprovementsView.tsx";
import { Permissions } from "src/api/generated/iam/permissions.ts";
import { useHasPermission } from "src/components/views/erp/common/userPermissions.ts";

export interface ControlChartEventsDataGridProps extends RefreshableElementProps {
	controlChartId?: number;
	chartGroupId?: number;
}

export const ControlChartEventsDataGrid = ({ controlChartId, chartGroupId }: ControlChartEventsDataGridProps) => {
	const { tenantConfig } = useTenantCustomizations();
	const { openDialog } = useGenericDialog();
	const hasPermission = useHasPermission();

	const { graphViewApiRef, eventsDataGridRefreshRef } = useContext(ControlChartInspectingViewContext) ?? {};

	const { dataGridProps, onlySelectedRow, refreshData } = useServerSideDataGridModel({
		gridId: "FD6EC55B3BC14A9BD",
		fetchData: (params) =>
			ControlChartEventsDataGridApi.getGridData({
				controlChartId,
				chartGroupId,
				...params,
			}),
		initialParams: {},
		getRowId: (row) => row.recordId,
		refreshRef: eventsDataGridRefreshRef,
	});

	return (
		<ControlledAsyncDataGrid<ControlChartEventsDataGridApi_GridRow>
			disableColumnFilter
			columns={[
				...(chartGroupId == null ?
					[]
				:	[
						textColumn<ControlChartEventsDataGridApi_GridRow>({
							field: "controlChartName",
							headerName: i18n.t("control_chart"),
							sortable: false,
							width: 200,
						}),
					]),
				dateTimeColumn({
					field: "recordObservationTime",
					headerName: i18n.t("recorded_at"),
					sortable: false,
					width: 150,
				}),
				textColumn({
					field: "ocs",
					headerName: i18n.t("reason"),
					sortable: false,
					width: 100,
				}),
				textColumn({
					field: "description",
					headerName: i18n.t("description"),
					sortable: false,
					width: 150,
				}),
				dateTimeColumn({
					field: "createdAt",
					headerName: i18n.t("created_at"),
					sortable: false,
					width: 120,
				}),
				textColumn({
					field: "createdBy",
					headerName: i18n.t("created_by"),
					sortable: false,
					width: 120,
				}),
				integerColumn({
					field: "recordId",
					headerName: i18n.t("id"),
					sortable: false,
				}),
			]}
			actionBarComponents={
				<>
					{tenantConfig.documentsEnabled && (
						<>
							<DocumentsOfObjectButton
								objectRef={nullableAavoObjectRef("SPC_RECORD", onlySelectedRow?.recordId)}
							/>
							<UploadDocumentsToSpcRecordButton
								disabled={!onlySelectedRow}
								recordId={onlySelectedRow?.recordId}
							/>
						</>
					)}
					{controlChartId != null && (
						<FocusControlChartGraphViewToRecordButton
							disabled={!onlySelectedRow}
							recordObservationTime={parseDayJsNullable(onlySelectedRow?.recordObservationTime)}
							graphViewApiRef={graphViewApiRef}
						/>
					)}
				</>
			}
			rowContextMenuComponents={({ row }) => [
				tenantConfig.spc.processImprovementsEnabled &&
					hasPermission(Permissions.Spc.ProcessImprovements.MANAGE) && [
						row.processImprovementId == null && (
							<AsyncMenuButton
								key={"connectToProcessImprovement"}
								label={i18n.t("connect_to_an_improvement")}
								icon={faLink}
								onClick={() => {
									openDialog(({ closeDialog }) => ({
										title: i18n.t("explanations"),
										size: "xl",
										agentContext: {
											key: defineAgentUiContextKey("spc.controlChart.eventConnect.dialog"),
											data: { controlChartId: row.controlChartId, rawRecordId: row.recordId },
											pathSegments: [i18n.t("connect_to_an_improvement")],
										},
										content: (
											<ConnectProcessImprovementToRawRecordView
												rawRecordId={row.recordId}
												onSubmit={async () => {
													await refreshData();
													await closeDialog();
												}}
											/>
										),
									}));
								}}
							/>
						),
						row.processImprovementId != null && (
							<AsyncMenuButton
								key={"openImprovement"}
								label={i18n.t("open_improvement")}
								icon={faLink}
								onClick={() => {
									openDialog({
										title: i18n.t("explanations"),
										size: "xl",
										agentContext: {
											key: defineAgentUiContextKey("spc.controlChart.eventImprovement.dialog"),
											data: { processImprovementId: row.processImprovementId ?? null },
											pathSegments: [i18n.t("open_improvement")],
										},
										content: (
											<ProcessImprovementsView
												onlyProcessImprovementId={row.processImprovementId}
												selectFirstImprovementOnLoad={true}
											/>
										),
									});
								}}
							/>
						),
					],
			]}
			agentContext={{
				key: defineAgentUiContextKey("spc.controlChart.events.grid"),
				data: { controlChartId: controlChartId ?? null, chartGroupId: chartGroupId ?? null },
				rowFields: ["recordId", "controlChartId", "processImprovementId", "createdBy"],
				selectedRowPathSegment: (row) => `SPC ${row.recordId}`,
				selectedRowsPathSegment: (count) => i18n.t("rows_selected_count", { count }),
			}}
			{...dataGridProps}
		/>
	);
};
